import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import QuotaBanner from './QuotaBanner'

interface Props {
  requireAdmin?: boolean
}

/**
 * Wraps routes that require authentication.
 *
 * - Calls fetchUser() once if auth status is unknown.
 * - Shows a blank loading state while resolving.
 * - Redirects unauthenticated users to /.
 * - Redirects non-admin users away from admin routes to /home.
 */
export default function ProtectedRoute({ requireAdmin = false }: Props) {
  const { status, user, fetchUser } = useAuthStore()
  const location = useLocation()

  useEffect(() => {
    if (status === 'unknown') {
      fetchUser()
    }
  }, [status, fetchUser])

  if (status === 'unknown') {
    return <div className="min-h-screen bg-stone-50" aria-label="Loading" />
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/" state={{ from: location }} replace />
  }

  if (requireAdmin && !user?.isAdmin) {
    return <Navigate to="/home" replace />
  }

  // The quota banner is the old design. The new shell has no AI steps yet (redesign F2).
  return (
    <>
      {user?.uiVersion !== 'new' && <QuotaBanner />}
      <Outlet />
    </>
  )
}
